import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import BondDialogDocumentsSection from "./BondDialogDocumentsSection";

const BondArchivesCancelDialog = ({
  cancelDialogOpen,
  setCancelDialogOpen,
  isCancelling,
  isMarkingCancellationComplete,
  isMarkingToBeCancelled,
  selectedBondToCancel,
  getCancellationInstructionsList,
  handleCancellationFileInputChange,
  cancellationFile,
  handleMarkToBeCancelled,
  handleMarkCancellationComplete,
  handleRevertCancellation,
  handleSubmitCancellationRequest,
}) => {
  return (
    <Dialog
      open={cancelDialogOpen}
      onClose={() => !isCancelling && !isMarkingCancellationComplete && setCancelDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2.5,
          fontWeight: 700,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Bond Cancellation
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => !isCancelling && !isMarkingCancellationComplete && setCancelDialogOpen(false)}
          edge="end"
          size="small"
          disabled={isCancelling || isMarkingCancellationComplete}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5 }}>
        {selectedBondToCancel && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Cancellation Details
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <strong>Bond Number:</strong> {selectedBondToCancel.bond_number ?? "N/A"}
            </Typography>
            <Typography sx={{ mb: 2 }}>
              <strong>Bond Type:</strong> {selectedBondToCancel.bond_type}
            </Typography>

            <Divider sx={{ mb: 2 }} />
            <BondDialogDocumentsSection
              bond={selectedBondToCancel}
              getCancellationInstructionsList={getCancellationInstructionsList}
              cancellationFile={cancellationFile}
              disableUpload={isCancelling || isMarkingCancellationComplete}
              onFileInputChange={handleCancellationFileInputChange}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: "100%" }}
        >
          {selectedBondToCancel && (
            <Stack direction="row" spacing={1.25}>
              <Button
                onClick={handleMarkToBeCancelled}
                color="warning"
                variant="outlined"
                size="small"
                disabled={isCancelling || isMarkingCancellationComplete || isMarkingToBeCancelled}
                startIcon={isMarkingToBeCancelled && <CircularProgress size={14} />}
              >
                {isMarkingToBeCancelled ? "Marking..." : "Mark To Be Cancelled"}
              </Button>
              <Button
                onClick={handleMarkCancellationComplete}
                color="error"
                variant="outlined"
                size="small"
                disabled={isCancelling || isMarkingCancellationComplete}
                startIcon={isMarkingCancellationComplete && <CircularProgress size={14} />}
              >
                {isMarkingCancellationComplete ? "Marking Cancelled..." : "Mark as Cancelled"}
              </Button>

              <Button
                onClick={handleRevertCancellation}
                color="primary"
                variant="outlined"
                size="small"
                disabled={isCancelling || isMarkingCancellationComplete}
                startIcon={isMarkingCancellationComplete && <CircularProgress size={14} />}
              >
                {isMarkingCancellationComplete ? "Reverting..." : "Revert Cancellation"}
              </Button>
            </Stack>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setCancelDialogOpen(false)} disabled={isCancelling || isMarkingCancellationComplete}>
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!cancellationFile || isCancelling || isMarkingCancellationComplete}
              onClick={handleSubmitCancellationRequest}
            >
              {isCancelling ? <CircularProgress size={22} color="inherit" /> : "Submit Cancellation"}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

BondArchivesCancelDialog.propTypes = {
  cancelDialogOpen: PropTypes.bool.isRequired,
  setCancelDialogOpen: PropTypes.func.isRequired,
  isCancelling: PropTypes.bool.isRequired,
  isMarkingCancellationComplete: PropTypes.bool.isRequired,
  isMarkingToBeCancelled: PropTypes.bool.isRequired,
  selectedBondToCancel: PropTypes.object,
  getCancellationInstructionsList: PropTypes.func.isRequired,
  handleCancellationFileInputChange: PropTypes.func.isRequired,
  cancellationFile: PropTypes.object,
  handleMarkToBeCancelled: PropTypes.func.isRequired,
  handleMarkCancellationComplete: PropTypes.func.isRequired,
  handleRevertCancellation: PropTypes.func.isRequired,
  handleSubmitCancellationRequest: PropTypes.func.isRequired,
};

export default BondArchivesCancelDialog;
